import { CalendarClock, CheckCircle2, MessageSquareText, PackageCheck, UserRound, X } from "lucide-react";
import type { Contact, CustomerOrder } from "../../types";
import { formatDate, formatDateTime } from "../../lib/format";
import { Button } from "../ui";
import { customerStatusOptions, orderStatusOptions } from "./WorkModals";

interface LinkedEvent {
  id: string;
  title: string;
  occurred_at: string;
  content?: string;
}

export function CustomerDetailModal({
  contact,
  orders,
  events,
  onClose
}: {
  contact: Contact;
  orders: CustomerOrder[];
  events: LinkedEvent[];
  onClose: () => void;
}) {
  const statusLabel = customerStatusOptions.find(([value]) => value === contact.customer_status)?.[1] ?? contact.customer_status;
  const importanceLabel = { S: "重点", A: "重要", B: "一般", C: "次要" }[contact.importance] ?? "一般";

  return (
    <div className="modal-backdrop customer-drawer-backdrop" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
      <aside className="modal customer-detail-modal customer-drawer" role="dialog" aria-modal="true" aria-label={`${contact.name} 客户详情`}>
        <div className="modal__header">
          <div>
            <span className="eyebrow">CUSTOMER · 客户画像</span>
            <h2>{contact.name}</h2>
            <p>{[contact.organization, contact.country].filter(Boolean).join(" · ") || "未填写公司与市场"}</p>
          </div>
          <button className="modal__close" onClick={onClose} aria-label="关闭">✕</button>
        </div>

        <div className="customer-detail__tags">
          <span className="customer-detail__tag">{statusLabel}</span>
          <span className={`customer-detail__tag customer-detail__tag--${contact.importance.toLowerCase()}`}>重要性：{importanceLabel}</span>
          {contact.contact_channel && (
            <span className="customer-detail__tag"><MessageSquareText size={12} />{contact.contact_channel}{contact.channel_account ? ` · ${contact.channel_account}` : ""}</span>
          )}
        </div>

        <section className="customer-detail__section customer-detail__business">
          <h3><PackageCheck size={15} /> 业务档案</h3>
          <dl>
            <div><dt>主营</dt><dd>{contact.main_products || "待登记"}</dd></div>
            <div><dt>采购季节</dt><dd>{contact.purchase_season || "待登记"}</dd></div>
            <div><dt>采购预算</dt><dd>{contact.annual_purchase || "待登记"}</dd></div>
            <div><dt>价格敏感度</dt><dd>{contact.price_sensitivity || "待登记"}</dd></div>
            <div><dt>MOQ 接受</dt><dd>{contact.moq_acceptance || "待登记"}</dd></div>
            <div><dt>沟通风格</dt><dd>{contact.comm_style || "待登记"}</dd></div>
            <div><dt>付款习惯</dt><dd>{contact.payment_habit || "待登记"}</dd></div>
            <div><dt>风险标签</dt><dd>{contact.risk_tags || "无"}</dd></div>
          </dl>
        </section>

        <section className="customer-detail__section">
          <h3><UserRound size={15} /> 客户背景</h3>
          <p>{contact.context || "还没有记录客户背景。下次聊天后可以让我帮你补上。"}</p>
        </section>

        <section className="customer-detail__section">
          <h3><CalendarClock size={15} /> 下一步</h3>
          <div className="customer-detail__next">
            <strong>{contact.next_action || "尚未安排明确动作"}</strong>
            {contact.next_followup && <span>跟进日期：{formatDate(contact.next_followup)}</span>}
          </div>
        </section>

        <section className="customer-detail__section">
          <h3><PackageCheck size={15} /> 订单（{orders.length}）</h3>
          {orders.length ? (
            <div className="customer-detail__orders">
              {orders.map((order) => (
                <div key={order.id} className="customer-detail__order">
                  <div>
                    <strong>{order.title}</strong>
                    <small>{order.order_code || "未填编号"} · {orderStatusOptions.find(([value]) => value === order.status)?.[1] ?? order.status}</small>
                  </div>
                  {order.next_action && <span>{order.next_action}</span>}
                </div>
              ))}
            </div>
          ) : (
            <p className="customer-detail__empty">还没有订单记录。</p>
          )}
        </section>

        <section className="customer-detail__section">
          <h3><CheckCircle2 size={15} /> 跟进记录（{events.length}）</h3>
          {events.length ? (
            <div className="customer-detail__timeline">
              {events.map((event) => (
                <div key={event.id} className="customer-detail__event">
                  <i />
                  <div>
                    <strong>{event.title}</strong>
                    <small>{formatDateTime(event.occurred_at)}</small>
                    {event.content && <p>{event.content}</p>}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="customer-detail__empty">还没有跟进记录。</p>
          )}
        </section>

        <div className="modal__actions">
          <Button variant="secondary" onClick={onClose}>关闭</Button>
        </div>
      </aside>
    </div>
  );
}
